import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";

import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import SurveyDataBlockModal from "@/features/survey/form/components/SurveyDataBlockModal";
import { EntitiesCondition } from "@/shared/utils/constants.js";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import AddDocumentIcon from "@/assets/icons/add-document.svg?react";
import SurveyIcon from "@/assets/icons/survey-icon.svg?react";
import { getEvidenceTitle } from "@/domains/evidence/hooks/useEvidenceFields";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { OptionEntitiesProps } from "@/features/survey/form/types";
import { DataBlockListResponse } from "@/redux/datablocks/types";
import ExternalLink from "@/assets/icons/external-link.svg?react";
import Close from "@/assets/icons/x.svg?react";

const Evidence = ({
  option,
  answer,
  handleSubmit,
  isLoading = false,
}: OptionEntitiesProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [dataBlocksModelOpen, setDataBlocksModelOpen] = useState(false);
  const sampleOrganizationId = searchParams.get("sampleOrganizationId");

  const handleRemoveBlock = async (blockId: number) => {
    const newBlocks = answer.attachedBlocks.filter((i) => i.id !== blockId);
    handleSubmit(newBlocks);
  };

  const handleAttachBlocks = async (blocks: DataBlockListResponse[]) => {
    await handleSubmit(blocks);
    setDataBlocksModelOpen(false);
  };

  const requiredDataBlockTypes = useMemo(() => {
    if (option.optionEntities.length > 0) {
      return option.optionEntities.map((i) => i.dataTemplate.title);
    }
    return [t("survey.labels.all")];
  }, [option]);

  const requiredDataTemplateIds = useMemo(() => {
    return option.optionEntities.map((i) => i.dataTemplate.id);
  }, [option]);

  return (
    <div className="flex flex-col gap-250">
      {requiredDataBlockTypes && (
        <p className="flex gap-100 items-center body-m-regular">
          <BaseSvgRenderer component={SurveyIcon} width="16" height="16" />
          {t(
            option.entitiesCondition === EntitiesCondition.AND.value
              ? "survey.labels.dataBlockTypesRequired"
              : "survey.labels.dataBlockTypesAccepted",
            {
              plural: requiredDataBlockTypes.length === 1 ? "" : "s",
            },
          )}{" "}
          {requiredDataBlockTypes.join(", ")}
        </p>
      )}
      {!sampleOrganizationId && (
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          leadingIcon={
            <BaseSvgRenderer component={AddDocumentIcon} stroke="white" />
          }
          onClick={() => setDataBlocksModelOpen(true)}
        >
          {t("survey.labels.addEvidence")}
        </BaseButton>
      )}

      {isLoading ? (
        <div className="flex items-center gap-200">
          <BaseSpinner size="extra-small" isFullWidth={false} />
          <p className="body-m-regular">{t("survey.labels.loadingEvidence")}</p>
        </div>
      ) : answer && answer.attachedBlocks.length > 0 ? (
        <div className="option-entity-attached-container flex flex-col gap-200">
          <h4 className="option-entity-attached-header-label body-m-regular">
            {t("survey.labels.attachedEvidence")} (
            {answer.attachedBlocks.length})
          </h4>
          {answer.attachedBlocks.map((block) => {
            const title = getEvidenceTitle(block);
            return (
              <div
                key={block.id}
                className="option-entity-attached-content-container flex justify-between items-center content-center self-stretch"
              >
                <div>
                  <h4 className="option-entity-attached-item-title body-m-medium">
                    {title}
                  </h4>

                  {!sampleOrganizationId && (
                    <p className="option-entity-attached-item-description body-s-regular">
                      {t("survey.labels.lastModified", {
                        context:
                          [
                            block.updatedAt ? "date" : "",
                            block.createdBy?.fullName ? "user" : "",
                          ]
                            .filter(Boolean)
                            .join("_") || undefined,
                        date: DateTime.fromISO(
                          block.updatedAt || "",
                        ).toLocaleString(DateTime.DATE_MED),
                        user: block.createdBy?.fullName || "",
                      })}
                    </p>
                  )}
                </div>

                <div className="flex gap-100 items-center">
                  <BaseButton
                    size={BUTTON_SIZES.SMALL}
                    variant={BUTTON_VARIANTS.LINK}
                    onClick={() =>
                      window.open(`/evidence/${block.id}`, "_blank")
                    }
                  >
                    <BaseSvgRenderer
                      component={ExternalLink}
                      width="20"
                      height="20"
                    />
                  </BaseButton>

                  {!sampleOrganizationId && (
                    <BaseButton
                      size={BUTTON_SIZES.SMALL}
                      variant={BUTTON_VARIANTS.LINK}
                      onClick={() => handleRemoveBlock(block.id)}
                    >
                      <BaseSvgRenderer
                        component={Close}
                        width="20"
                        height="20"
                      />
                    </BaseButton>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <SurveyDataBlockModal
        open={dataBlocksModelOpen}
        setOpen={setDataBlocksModelOpen}
        initialData={{
          question: option.question,
        }}
        filters={{
          dataTemplates: requiredDataTemplateIds,
          prioritizeQuestion: option.question,
        }}
        selectedBlocks={answer?.attachedBlocks || []}
        setSelectedBlocks={handleAttachBlocks}
      />
    </div>
  );
};

export default Evidence;
